import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import type { LinkStatus } from "./LinkProgressSingleDialog";

interface LinkProgressDialogProps {
  open: boolean;
  status?: LinkStatus;
  onComplete: () => void;
}

const STEP_DURATIONS = [350, 800, 1200, 400];
const SUCCESS_AUTO_CLOSE_MS = 600;
const ERROR_AUTO_CLOSE_MS = 1800;

const LinkProgressDialog = ({
  open,
  status = "pending",
  onComplete,
}: LinkProgressDialogProps) => {
  const { t } = useTranslation();
  const [completedSteps, setCompletedSteps] = useState<number>(0);
  const [finished, setFinished] = useState(false);
  const stepTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const STEPS = [
    t("coachingNavigator.coachingCalendar.progressStep1"),
    t("coachingNavigator.coachingCalendar.progressStep2"),
    t("coachingNavigator.coachingCalendar.progressStep3"),
  ];

  const finalStepErrorText = t(
    "coachingNavigator.coachingCalendar.progressError",
    {
      defaultValue: "Linking failed. Please try again.",
    },
  );
  const finalStepPendingText = t(
    "coachingNavigator.coachingCalendar.progressFinalizing",
    {
      defaultValue: "Finalizing linkage…",
    },
  );

  const allStepsLength = STEPS.length + 1;

  const runProgress = useCallback(() => {
    setCompletedSteps(0);
    setFinished(false);

    let stepIndex = 0;
    const advanceStep = () => {
      if (stepTimeoutRef.current) {
        clearTimeout(stepTimeoutRef.current);
      }
      stepTimeoutRef.current = setTimeout(() => {
        stepIndex += 1;
        setCompletedSteps(stepIndex);

        if (stepIndex < allStepsLength) {
          advanceStep();
        } else {
          setFinished(true);
        }
      }, STEP_DURATIONS[stepIndex]);
    };

    advanceStep();
  }, [allStepsLength]);

  useEffect(() => {
    if (open) {
      runProgress();
    }
    return () => {
      if (stepTimeoutRef.current) {
        clearTimeout(stepTimeoutRef.current);
        stepTimeoutRef.current = null;
      }
    };
  }, [open, runProgress]);

  // Auto-close only after the fake loading finishes AND the real linkage has settled.
  useEffect(() => {
    if (!finished || status === "pending") return;
    const delay =
      status === "error" ? ERROR_AUTO_CLOSE_MS : SUCCESS_AUTO_CLOSE_MS;
    const timer = window.setTimeout(() => {
      onCompleteRef.current();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [finished, status]);

  if (!open) {
    return null;
  }

  const resolved = finished && status !== "pending";
  const finalText =
    resolved && status === "error" ? finalStepErrorText : finalStepPendingText;
  const allSteps = [...STEPS, finalText];

  const renderStepIcon = (index: number) => {
    const isFinal = index === allSteps.length - 1;
    if (!isFinal) {
      if (completedSteps > index) {
        return <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 32 }} />;
      }
      if (completedSteps === index) {
        return <CircularProgress size={28} sx={{ color: "#242C63" }} />;
      }
      return <Box sx={{ width: 28, height: 28 }} />;
    }
    // Final step: defer to real linkage status once the fake animation reaches it.
    const finalReached = completedSteps >= allSteps.length;
    if (finalReached && status === "error") {
      return <ErrorOutlineIcon sx={{ color: "error.main", fontSize: 32 }} />;
    }
    if (finalReached && status === "success") {
      return <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 32 }} />;
    }
    if (finalReached || completedSteps === index) {
      return <CircularProgress size={28} sx={{ color: "#242C63" }} />;
    }
    return <Box sx={{ width: 28, height: 28 }} />;
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "8px", bgcolor: "#fff" } } }}
    >
      <DialogTitle>
        {t("coachingNavigator.coachingCalendar.creatingLinkage")}
      </DialogTitle>
      <DialogContent dividers>
        {allSteps.map((step, index) => {
          const isFinal = index === allSteps.length - 1;
          const isErrorFinal = isFinal && resolved && status === "error";
          return (
            <Box key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  py: 2,
                  px: 1,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {renderStepIcon(index)}
                </Box>
                <Typography
                  variant="body1"
                  sx={[
                    isErrorFinal
                      ? {
                          color: "error.main",
                        }
                      : completedSteps >= index
                        ? {
                            color: "text.primary",
                          }
                        : {
                            color: "text.disabled",
                          },
                    completedSteps === index
                      ? {
                          fontWeight: 500,
                        }
                      : {
                          fontWeight: 400,
                        },
                  ]}
                >
                  {step}
                </Typography>
              </Box>
              {index < allSteps.length - 1 && (
                <Divider sx={{ borderColor: "#DFE1E3" }} />
              )}
            </Box>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

export default LinkProgressDialog;
